import {
  codeFullWarningAtom,
  presentFestivalReminderAtom,
  presentB2BActivatedModalAtom,
  presentB2CPaidModalAtom,
  presentCreateOfferModalAtom,
  presentSubscriptionCodeModalAtom,
  presentInsuredDetailsAtom,
  presentLoginSheet,
  presentGuestSignupPromptAtom,
  presentNewListingPrompt,
  presentNotificationModalAtom,
  presentPayWallAtom,
  presentSignUpSheet,
  presentUserMetaModalAtom,
} from 'app/atoms/ui';
import { navigationRef } from 'app/lib/root-navigation';
import type { NavigationState } from '@react-navigation/native';
import { getDefaultStore } from 'jotai';
import { getIsLargeUI } from 'app/utils/web-helpers';

/** Root-stack routes that overlay the main app and should block contextual tutorials. */
const BLOCKING_ROOT_ROUTES = new Set([
  'intro',
  'presentCommunity',
  'tutorialModal',
  'notificationExplainer',
  'chooseUserType',
  'publicUserWelcome',
  'listingModal',
  'userProfileModal',
  'newAddressModal',
  'freeSub',
  'trialPromo',
  'payment',
  'payment-success',
  'notificationSuperChanged',
  'loanFee',
  'offerAvailability',
  'refrainOfferAvailabilityPopup',
  'paywall',
  'help',
]);

export interface BlockingModalOptions {
  /** Allow the listing detail modal (ContactMember tutorial runs inside it). */
  allowListingModal?: boolean;
}

function isBlockingAtomValue(value: unknown) {
  return Boolean(value);
}

function isNotificationPermissionModalBlocking() {
  const store = getDefaultStore();
  if (!isBlockingAtomValue(store.get(presentNotificationModalAtom))) {
    return false;
  }
  // NotificationWarnModal is not shown on largeUI web; the atom can stay true forever.
  return !getIsLargeUI();
}

export function isBlockingModalAtomVisible() {
  const store = getDefaultStore();

  return (
    isNotificationPermissionModalBlocking() ||
    isBlockingAtomValue(store.get(presentFestivalReminderAtom)) ||
    isBlockingAtomValue(store.get(presentPayWallAtom)) ||
    isBlockingAtomValue(store.get(presentB2BActivatedModalAtom)) ||
    isBlockingAtomValue(store.get(presentB2CPaidModalAtom)) ||
    isBlockingAtomValue(store.get(presentSubscriptionCodeModalAtom)) ||
    isBlockingAtomValue(store.get(presentUserMetaModalAtom)) ||
    isBlockingAtomValue(store.get(presentCreateOfferModalAtom)) ||
    isBlockingAtomValue(store.get(presentNewListingPrompt)) ||
    isBlockingAtomValue(store.get(presentSignUpSheet)) ||
    isBlockingAtomValue(store.get(presentLoginSheet)) ||
    isBlockingAtomValue(store.get(presentGuestSignupPromptAtom)) ||
    isBlockingAtomValue(store.get(presentInsuredDetailsAtom)) ||
    isBlockingAtomValue(store.get(codeFullWarningAtom))
  );
}

function isBlockingRoute(routeName: string, options?: BlockingModalOptions) {
  if (options?.allowListingModal && routeName === 'listingModal') {
    return false;
  }
  return BLOCKING_ROOT_ROUTES.has(routeName);
}

function hasBlockingRouteInStack(state: NavigationState | undefined, options?: BlockingModalOptions): boolean {
  if (!state) return false;

  const activeRoute = state.routes[state.index];
  if (!activeRoute) return false;

  if (isBlockingRoute(activeRoute.name, options)) {
    return true;
  }

  if (activeRoute.state && hasBlockingRouteInStack(activeRoute.state as NavigationState, options)) {
    return true;
  }

  return false;
}

export function isBlockingNavigationModalVisible(options?: BlockingModalOptions) {
  if (!navigationRef.isReady()) {
    return false;
  }

  return hasBlockingRouteInStack(navigationRef.getRootState(), options);
}

export function isBlockingModalVisible(options?: BlockingModalOptions) {
  return isBlockingModalAtomVisible() || isBlockingNavigationModalVisible(options);
}
